import { nameColumn } from "../../../../../layouts/Table/columns/nameColumn";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Avatar, Button, Flex, Group, ThemeIcon } from "@mantine/core";
import MainTable from "../../../../../layouts/Table/MainTable";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import numberColumn from "../../../../../layouts/Table/columns/numberColumn";
import { sharingColumn } from "../../../../../layouts/Table/columns/sharingColumn";
import { useListingLoader } from "../../../../../components/hooks/ListingHooks";
import { IconChevronDown, IconDownload, IconUsers } from "@tabler/icons-react";
import { useListStore } from "../../../../../stores/ListStore";
import SelectionDeleter from "../../../../../components/TableComponents/SelectionDeleter";
import {
  deleteBlacklist,
  editBlacklist,
  getBlacklists,
} from "../../../../../api/BlacklistAPI";
import { useNavigate, useParams } from "react-router-dom";
import { getRowClasses } from "../listing";
import ItemDeleter from "../../../../../components/TableComponents/ItemDeleter";
import dateColumn from "../../../../../layouts/Table/columns/dateColumn";
import { blacklistTypeColumn } from "./blacklistTypeColumn";

const BlacklistsListing = () => {
  const lang = useLanguage((s) => s.language);
  const apiRef = useGridApiRef();
  const { blacklistId } = useParams();
  const listId = useMemo(() => parseInt(blacklistId), [blacklistId]);

  const columns = useMemo(() => {
    let result = [
      numberColumn(),
      nameColumn({
        disableLink: true,
        handleClick: () => { },
        editFunction: () => editBlacklist,
        apiRef,
      }),
      blacklistTypeColumn({ apiRef }),
      sharingColumn({ apiRef, editFunction: () => editBlacklist }),
      dateColumn()
    ];

    return result;
  }, [apiRef]);

  const loading = useListStore((s) => s.listingLoad);
  const setLoading = useListStore((s) => s.setListingLoad);
  const [rows, setRows] = useState([]);

  const actionsCell = useCallback(
    ({ row }) => {
      return (
        <Group wrap="nowrap" gap={4}>
          <ItemDeleter
            title_bold={`${row.name} #${row.id}`}
            handleDelete={deleteBlacklist}
            deletedMessage={lang.blacklists.delete_success}
            id={row.id}
            apiRef={apiRef}
            onFinish={() => setLoading(true)}
          />

          <IconChevronDown
            size={24}
            className="bump"
            opacity={listId === row.id ? 1 : 0.4}
            style={{
              transform: listId === row.id ? "rotate(-90deg)" : "none",
              transition: "all ease .3s",
            }}
            onClick={() => handleRowClick({ row })}
          />
        </Group>
      );
    },
    [listId]
  );

  const toolbarActions = useCallback(
    ({ selectedIds }) => {
      return (
        <>
          <Button
            variant="light"
            color="primary"
            disabled={selectedIds.length === 0}
            leftSection={<IconDownload size={18} />}
            justify="start"
            className="to-blue"
          >
            {lang.contact_lists.download}
          </Button>

          <SelectionDeleter
            handleDelete={deleteBlacklist}
            apiRef={apiRef}
            setTableLoading={setLoading}
            ids={selectedIds}
            setRows={setRows}
          />
        </>
      );
    },
    [apiRef, lang.contact_lists.download]
  );

  const navigate = useNavigate();
  const handleRowClick = ({ row }) => {
    navigate(`/blacklists/${row.id}`);
  };

  useEffect(() => {
    setLoading(true);
  }, []);

  useListingLoader({
    loading,
    setLoading,
    listGetter: getBlacklists,
    responseField: "blacklists_list",
    setRows,
  });

  return (
    <Flex w={"100%"} pos={"relative"} flex={1} miw={380}>
      <Group
        pos={"absolute"}
        top={64}
        pt={8}
        w={"100%"}
        style={{
          borderTop: "1px solid #00000012",
        }}
      >
        <Button
          style={{ zIndex: 7 }}
          variant={blacklistId === "all" ? "filled" : "subtle"}
          fz={20}
          fw={600}
          radius={"md"}
          c={"black"}
          size="md"
          w={"100%"}
          justify="start"
          pl={7}
          h={52}
          leftSection={
            <ThemeIcon size={40} radius={"md"} color={blacklistId === "all" ? "white" : "primary"}>
              <IconUsers />
            </ThemeIcon>
          }
          onClick={() => {
            navigate("/blacklists/all");
          }}
        >
          {lang.prospects.all_blacklists}
        </Button>
      </Group>
      <MainTable
        apiRef={apiRef}
        columns={columns}
        rows={rows}
        loading={loading}
        setLoading={setLoading}
        id={"list-table"}
        hasFolder
        actionsCellWidth={75}
        disableGotoPage
        disableVisibleColumns
        defaultPerPage={15}
        disablePerPage
        onRowClick={handleRowClick}
        actionsCell={actionsCell}
        toolbarActions={toolbarActions}
        getRowClassName={(params) =>
          getRowClasses({ params, mainId: blacklistId, integerId: listId })
        }
        tableSx={{
          "& .MuiDataGrid-pinnedColumnHeaders": {
            border: "none !important",
            background: "none !important",
          },
          "& .MuiDataGrid-pinnedColumns": {
            border: "none !important",
            background: "none !important",
          },
          "& .MuiDataGrid-cell": {
            border: "none !important",
            borderRadius: 8,
          },
          "& .MuiDataGrid-columnHeaders": {
            border: "none !important",
            display: "flex",
          },
          "& .MuiDataGrid-main": {
            border: "none !important",
            borderRadius: 4,
            marginTop: 8,
            background: "none !important",
          },
          "& .table-row": {
            cursor: "pointer",
            borderRadius: 4,
            marginBottom: 1,
            background: "#fff !important",
            border: "1px solid var(--mantine-color-primary-1)",
          },
          "& .MuiDataGrid-pinnedColumns--right .table-row": {
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderLeft: "none",
          },
          "& .MuiDataGrid-pinnedColumns--left .table-row": {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderRight: "none",
          },
          "& .MuiDataGrid-virtualScrollerRenderZone .table-row": {
            borderRadius: 0,
            borderTopRightRadius: "0 !important",
            borderBottomRightRadius: "0 !important",
          },
          "& .MuiDataGrid-selectedRowCount": {
            visibility: "hidden",
          },
          "& .MuiPagination-root": {
            left: 0,
            transform: "none",
          },
          "& .Mui-selected": {
            color: "",
          },
          "& .MuiDataGrid-footerContainer": {
            border: 0,
          },
          "& .table-search": {
            width: "180px !important",
          },
        }}
      />
    </Flex>
  );
};

export default BlacklistsListing;
